import React, { useState, useEffect, useContext } from 'react'
import { useParams, useHistory, Link } from 'react-router-dom'
import { Layout } from '../../components/Layout'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Button } from 'primereact/button'
import { Message } from 'primereact/message'
import { Card } from 'primereact/card'
import { Context } from '../../Context'
import { GLOBALS } from '../../utils/Globals'
import { SchedulerService } from '../../services/Scheduler'
import './styles.scss'

export const CourseStudentUses = () => {
  const { courseId, userId } = useParams()
  const history = useHistory()
  const { isAuth: token } = useContext(Context)
  const [usesData, setUsesData] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    getStudentUses()
  }, [courseId, userId])

  const getStudentUses = async () => {
    try {
      setIsLoading(true)

      // Obtener reservas del usuario
      const schedulerResponse = await SchedulerService.getSchedulerByUser(userId, token)

      // Transformar los datos para que coincidan con la estructura esperada
      const transformedData = schedulerResponse.map((reservation) => {
        const startDate = new Date(reservation.startDate)
        const endDate = new Date(reservation.endDate)
        const totalHours = calculateTotalHours(startDate, endDate)

        return {
          id: reservation.id,
          reservationId: reservation.id,
          laboratoryName: reservation.laboratory?.name || 'Laboratorio no especificado',
          startDate: reservation.startDate,
          endDate: reservation.endDate,
          totalHours: totalHours,
        }
      })

      setUsesData(transformedData)
    } catch (e) {
      setError('Error al cargar las reservas del estudiante')
      console.error('Error:', e)
    } finally {
      setIsLoading(false)
    }
  }

  const calculateTotalHours = (startDate, endDate) => {
    const diffInMs = endDate.getTime() - startDate.getTime()
    const diffInHours = diffInMs / (1000 * 60 * 60)

    if (diffInHours < 1) {
      const minutes = Math.round(diffInHours * 60)
      return `${minutes} minutos`
    } else {
      const hours = Math.floor(diffInHours)
      const minutes = Math.round((diffInHours - hours) * 60)

      if (minutes === 0) {
        return `${hours} hora${hours !== 1 ? 's' : ''}`
      } else {
        return `${hours} hora${hours !== 1 ? 's' : ''} ${minutes} minuto${minutes !== 1 ? 's' : ''}`
      }
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const actionBodyTemplate = (rowData) => {
    return (
      <Link
        to={`${GLOBALS.menu.courses.link}/uses/${courseId}/student/${userId}/scheduler/${rowData.reservationId}`}
      >
        <Button
          type="button"
          icon="pi pi-eye"
          className="p-button-info action-button"
          tooltip="Ver detalles"
          tooltipOptions={{ position: 'top' }}
        />
      </Link>
    )
  }

  const columns = [
    {
      field: 'reservationId',
      header: 'ID de Reserva',
      sortable: true,
      filter: true,
      filterPlaceholder: 'Buscar por ID',
    },
    {
      field: 'laboratoryName',
      header: 'Nombre de Laboratorio',
      sortable: true,
      filter: true,
      filterPlaceholder: 'Buscar por laboratorio',
    },
    {
      field: 'startDate',
      header: 'Fecha de Inicio',
      sortable: true,
      body: (rowData) => formatDate(rowData.startDate),
    },
    {
      field: 'endDate',
      header: 'Fecha de Fin',
      sortable: true,
      body: (rowData) => formatDate(rowData.endDate),
    },
    {
      field: 'totalHours',
      header: 'Horas Totales',
      sortable: true,
    },
    {
      field: 'actions',
      header: 'Acciones',
      sortable: false,
      body: actionBodyTemplate,
    },
  ]

  if (isLoading) {
    return (
      <Layout>
        <div className="course-student-uses-container">
          <div className="loading-container">
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '4em' }}></i>
            <p>Cargando reservas del estudiante...</p>
          </div>
        </div>
      </Layout>
    )
  }

  if (error) {
    return (
      <Layout>
        <div className="course-student-uses-container">
          <Message severity="error" text={error} />
          <Button
            label="Volver"
            icon="pi pi-arrow-left"
            onClick={() => history.goBack()}
            className="p-button-secondary"
          />
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="course-student-uses-container">
        <div className="course-student-uses-header">
          <Button
            label="Volver"
            icon="pi pi-arrow-left"
            onClick={() => history.goBack()}
            className="p-button-secondary back-button"
          />
          <h1>
            <i className="pi pi-calendar"></i>
            Reservas del Estudiante
          </h1>
        </div>

        <Card className="uses-table-card">
          <DataTable
            value={usesData}
            paginator
            rows={50}
            rowsPerPageOptions={[10, 25, 50]}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            emptyMessage="No se encontraron reservas para este estudiante"
            className="p-datatable-custom"
          >
            {columns.map((col) => (
              <Column
                key={col.field}
                field={col.field}
                header={col.header}
                sortable={col.sortable}
                filter={col.filter}
                filterPlaceholder={col.filterPlaceholder}
                body={col.body}
              />
            ))}
          </DataTable>
        </Card>
      </div>
    </Layout>
  )
}
